import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { getAdminDashboard, adminExportUrl } from "../lib/adminPortal";
import { Download, Check, ArrowRight } from "lucide-react";
import { relativeDate } from "../lib/ownerUi";
import ContextStrip from "../components/ContextStrip";

/**
 * Platform Admin Dashboard.
 *
 * Scoped to admin's actual job: trust & safety (architect verification),
 * platform operations (code changes, revenue), and pipeline alerts. Anything
 * that's already on the City Staff dashboard (project pipeline, design
 * catalog distribution) is intentionally NOT duplicated here.
 *
 * Single bordered panel; hairlines divide sections; nothing floats.
 */
export default function AdminDashboard() {
  const { user } = useRole();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    setErr(null);
    getAdminDashboard(user.id)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(e));
    return () => {
      alive = false;
    };
  }, [user?.id]);

  if (err)
    return (
      <p style={{ color: "#A23B2E" }} data-testid="admin-dashboard-error">
        Could not load the dashboard.
      </p>
    );
  if (!data)
    return (
      <div
        style={{ color: "#6C6B85", fontStyle: "italic" }}
        data-testid="admin-dashboard-loading"
      >
        Loading platform operations…
      </div>
    );

  return (
    <section className="animate-fade-in-up" data-testid="admin-dashboard">
      <ContextStrip
        eyebrow="Platform Admin · Operations"
        title="Dashboard"
        subtitle="Trust & safety, platform operations, and stalled work. The City Staff dashboard owns project & catalog distributions; this view owns admin-only signal."
        actions={
          <a
            href={adminExportUrl("dashboard", user?.id, {})}
            className="wn-btn wn-btn-tertiary"
            data-testid="admin-dashboard-export"
          >
            <Download size={14} /> Export CSV
          </a>
        }
      />

      <StatsRow stats={data.stats} />

      <div
        className="mt-6"
        style={{
          background: "#FBFAFD",
          border: "1px solid #D9D8E4",
          borderRadius: 4,
          overflow: "hidden",
        }}
        data-testid="admin-dash-panel"
      >
        <SectionNeedsAttention sections={data.needs_attention.sections} />
        <Hairline />
        <SectionTwoColumn
          verification={data.verification_pipeline}
          events={data.recent_events.items}
        />
        <Hairline />
        <SectionUserActivity rows={data.user_activity.rows} />
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Tier 1 — Stat strip                                                     */
/* ────────────────────────────────────────────────────────────────────── */

function StatsRow({ stats }) {
  const tiles = [
    {
      key: "active_users",
      label: "Active users",
      value: stats.active_users,
      detail: "Excludes deactivated accounts",
    },
    {
      key: "verified_architects",
      label: "Verified architects",
      value: stats.verified_architects,
      detail:
        stats.pending_applications > 0
          ? `${stats.pending_applications} application${
              stats.pending_applications === 1 ? "" : "s"
            } awaiting action`
          : "All applications resolved",
    },
    {
      key: "code_changes",
      label: "Code change records",
      value: stats.pending_code_changes,
      detail:
        stats.pending_code_changes === 0
          ? "No active code-change records"
          : "Active records on file",
    },
    {
      key: "revenue_mtd",
      label: "Revenue MTD",
      value: stats.revenue_mtd > 0 ? `$${stats.revenue_mtd.toLocaleString()}` : "$0",
      detail:
        stats.plans_mtd > 0
          ? `${stats.plans_mtd} plan${stats.plans_mtd === 1 ? "" : "s"} this month`
          : "No transactions yet this month",
    },
  ];
  return (
    <div
      className="mt-6 grid gap-4"
      style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}
      data-testid="admin-stats"
    >
      {tiles.map((t) => (
        <div
          key={t.key}
          className="wn-card"
          style={{ padding: "14px 16px" }}
          data-testid={`admin-stat-${t.key}`}
        >
          <div
            className="wn-label"
            style={{ color: "#6C6B85", letterSpacing: "0.08em", fontSize: 10 }}
          >
            {t.label}
          </div>
          <div
            className="tabular-nums"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: 30,
              fontWeight: 600,
              color: "#1B1F4B",
              lineHeight: 1.1,
              marginTop: 4,
            }}
          >
            {t.value}
          </div>
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 12,
              marginTop: 4,
              lineHeight: 1.4,
            }}
          >
            {t.detail}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Section primitives                                                       */
/* ────────────────────────────────────────────────────────────────────── */

function Hairline() {
  return <div style={{ height: 1, background: "#D9D8E4" }} aria-hidden />;
}

function SectionBlock({ eyebrow, children, testId }) {
  return (
    <div
      data-testid={testId}
      style={{ padding: "20px 24px", background: "#FBFAFD" }}
    >
      {eyebrow && (
        <div
          className="wn-label"
          style={{
            color: "#8F3C23",
            letterSpacing: "0.10em",
            fontSize: 10.5,
            marginBottom: 14,
          }}
        >
          {eyebrow}
        </div>
      )}
      {children}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Tier 2 — Needs your attention                                          */
/* ────────────────────────────────────────────────────────────────────── */

function SectionNeedsAttention({ sections }) {
  const totalFlagged = sections.reduce((s, sec) => s + sec.total, 0);
  return (
    <SectionBlock
      eyebrow={`Needs your attention · ${totalFlagged} flagged`}
      testId="admin-needs-attention"
    >
      <div
        className="grid gap-5"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        }}
      >
        {sections.map((sec) => (
          <AttentionSection key={sec.key} section={sec} />
        ))}
      </div>
    </SectionBlock>
  );
}

function AttentionSection({ section }) {
  const empty = section.items.length === 0;
  return (
    <div data-testid={`admin-attention-${section.key}`}>
      <div className="flex items-baseline justify-between gap-2">
        <h3
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            fontSize: 14,
            color: "#1B1F4B",
            lineHeight: 1.3,
          }}
        >
          {section.label}
        </h3>
        <span
          className="tabular-nums"
          style={{
            color: empty ? "#9AA0C0" : "#A23B2E",
            fontSize: 11.5,
            fontWeight: 700,
          }}
        >
          {section.total}
        </span>
      </div>
      {empty ? (
        <p
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#4F6149",
            fontSize: 12.5,
            margin: "6px 0 0",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
          data-testid={`admin-attention-${section.key}-empty`}
        >
          <Check size={12} strokeWidth={2.4} />
          All clear.
        </p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {section.items.map((item) => (
            <AttentionItem
              key={item.id}
              section={section.key}
              item={item}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function AttentionItem({ section, item }) {
  // The item shape varies — pick the right secondary metric.
  let secondary = null;
  if (item.days != null) secondary = `${item.days}d`;
  else if (item.active_projects != null)
    secondary = `${item.active_projects} active`;
  else if (item.open_projects != null)
    secondary = `${item.open_projects} open`;
  return (
    <li
      className="flex items-baseline justify-between gap-3"
      style={{ fontSize: 12.5, color: "#272636" }}
    >
      <Link
        to={item.href}
        style={{ color: "#1B1F4B", fontWeight: 600, minWidth: 0 }}
        className="hover:underline truncate"
        data-testid={`admin-attention-${section}-${item.id}`}
      >
        {item.name}
      </Link>
      {secondary && (
        <span
          className="tabular-nums"
          style={{
            color: "#A23B2E",
            fontSize: 11.5,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {secondary}
        </span>
      )}
    </li>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Tier 3 — Two columns: Verification pipeline | Recent events             */
/* ────────────────────────────────────────────────────────────────────── */

function SectionTwoColumn({ verification, events }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 1px minmax(0, 1fr)",
      }}
      data-testid="admin-two-col"
    >
      <VerificationPipeline data={verification} />
      <div style={{ background: "#D9D8E4" }} aria-hidden />
      <RecentEvents items={events} />
    </div>
  );
}

const STATE_LABEL = {
  pending_verification: "Pending Verification",
  more_info_requested: "More Info Requested",
  approved: "Approved",
  rejected: "Rejected",
};

function VerificationPipeline({ data }) {
  const { counts, stages, total, approval_rate_pct, last_approval_at } = data;
  const max = Math.max(1, ...stages.map((s) => counts[s] || 0));
  // Reframe the "0%" reality so it doesn't read as alarming when it's just
  // historical: surface the recent activity context.
  const approvalContext = (() => {
    if (!last_approval_at) return "No approvals on record yet.";
    return `Last approval ${relativeDate(last_approval_at)}.`;
  })();

  return (
    <SectionBlock
      eyebrow={`Verification pipeline · ${total} application${total === 1 ? "" : "s"}`}
      testId="admin-verification"
    >
      <div className="space-y-2">
        {stages.map((stage) => {
          const c = counts[stage] || 0;
          const pct = max ? Math.round((c / max) * 100) : 0;
          return (
            <div key={stage} data-testid={`admin-vp-${stage}`}>
              <div
                className="flex items-baseline justify-between"
                style={{ fontSize: 12.5, color: "#272636" }}
              >
                <span>{STATE_LABEL[stage] || stage}</span>
                <span
                  className="tabular-nums"
                  style={{ color: "#1B1F4B", fontWeight: 700 }}
                >
                  {c}
                </span>
              </div>
              <div
                style={{
                  height: 4,
                  background: "#F3F2F6",
                  borderRadius: 2,
                  marginTop: 3,
                }}
              >
                <div
                  style={{
                    height: 4,
                    width: `${pct}%`,
                    background: indigoFor(pct),
                    borderRadius: 2,
                    transition: "width 240ms ease",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div
        className="mt-4 flex items-baseline justify-between gap-3"
        style={{
          paddingTop: 10,
          borderTop: "1px solid #E8E7EE",
        }}
        data-testid="admin-approval-rate"
      >
        <div>
          <div
            className="wn-label"
            style={{ color: "#6C6B85", fontSize: 10, letterSpacing: "0.06em" }}
          >
            Approval rate · last 90 days
          </div>
          <div
            className="tabular-nums"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: 20,
              fontWeight: 600,
              color: "#1B1F4B",
              lineHeight: 1.1,
              marginTop: 2,
            }}
          >
            {approval_rate_pct}%
          </div>
        </div>
        <div
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 12,
            textAlign: "right",
            maxWidth: 180,
            lineHeight: 1.4,
          }}
          data-testid="admin-approval-context"
        >
          {approvalContext}
        </div>
      </div>
      <Link
        to="/admin/applications"
        className="wn-btn wn-btn-tertiary inline-flex items-center mt-3"
        style={{ paddingLeft: 0, gap: 6, fontSize: 12 }}
        data-testid="admin-vp-open-queue"
      >
        Open application queue <ArrowRight size={12} />
      </Link>
    </SectionBlock>
  );
}

function RecentEvents({ items }) {
  return (
    <SectionBlock
      eyebrow={`Recent platform events · last ${items.length}`}
      testId="admin-recent-events"
    >
      {items.length === 0 ? (
        <p
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 13,
          }}
        >
          No platform events recorded yet.
        </p>
      ) : (
        <ul className="space-y-2.5" data-testid="admin-events-list">
          {items.map((a, i) => (
            <li
              key={a.id || i}
              className="flex items-start gap-2.5"
              data-testid={`admin-event-row-${i}`}
            >
              <span
                aria-hidden
                style={{
                  marginTop: 5,
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: "#B14E2F",
                  flexShrink: 0,
                }}
              />
              <div className="min-w-0 flex-1">
                <div
                  className="flex items-baseline justify-between gap-2 flex-wrap"
                  style={{ fontSize: 12.5, color: "#272636", lineHeight: 1.4 }}
                >
                  <span>
                    <strong style={{ color: "#1B1F4B" }}>
                      {prettyAction(a.action)}
                    </strong>
                    {" · "}
                    {(a.actor && a.actor.name) || a.actor_id || "System"}
                  </span>
                  <span
                    className="tabular-nums"
                    style={{
                      fontFamily: '"Newsreader", Georgia, serif',
                      fontStyle: "italic",
                      color: "#6C6B85",
                      fontSize: 11.5,
                    }}
                  >
                    {relativeDate(a.at)}
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      <Link
        to="/admin/audit"
        className="wn-btn wn-btn-tertiary inline-flex items-center mt-3"
        style={{ paddingLeft: 0, gap: 6, fontSize: 12 }}
        data-testid="admin-events-open-audit"
      >
        Open audit trail <ArrowRight size={12} />
      </Link>
    </SectionBlock>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Tier 4 — User activity table                                            */
/* ────────────────────────────────────────────────────────────────────── */

function SectionUserActivity({ rows }) {
  return (
    <SectionBlock eyebrow="User activity by role" testId="admin-user-activity">
      <div style={{ overflow: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 12.5,
          }}
          data-testid="admin-user-activity-table"
        >
          <thead>
            <tr style={{ borderBottom: "1px solid #D9D8E4" }}>
              <ThEl>Role</ThEl>
              <ThEl align="right">Total</ThEl>
              <ThEl align="right">Active 30d</ThEl>
              <ThEl align="right">Deactivated</ThEl>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.role}
                data-testid={`admin-user-activity-row-${r.role}`}
                style={{ borderBottom: "1px solid #E8E7EE" }}
              >
                <TdEl>
                  <span style={{ color: "#1B1F4B", fontWeight: 600 }}>
                    {r.role.replace(/_/g, " ")}
                  </span>
                </TdEl>
                <TdEl align="right">{r.total}</TdEl>
                <TdEl
                  align="right"
                  color={r.active_30d > 0 ? "#4F6149" : "#9AA0C0"}
                >
                  {r.active_30d}
                </TdEl>
                <TdEl
                  align="right"
                  color={r.deactivated > 0 ? "#A23B2E" : "#9AA0C0"}
                >
                  {r.deactivated}
                </TdEl>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionBlock>
  );
}

function ThEl({ children, align = "left" }) {
  return (
    <th
      scope="col"
      style={{
        padding: "8px 14px",
        textAlign: align,
        color: "#6C6B85",
        fontSize: 10,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        fontWeight: 700,
      }}
    >
      {children}
    </th>
  );
}

function TdEl({ children, align = "left", color = "#272636" }) {
  return (
    <td
      style={{
        padding: "9px 14px",
        textAlign: align,
        color,
        ...(align === "right" ? { fontVariantNumeric: "tabular-nums" } : {}),
      }}
    >
      {children}
    </td>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Helpers                                                                 */
/* ────────────────────────────────────────────────────────────────────── */

function indigoFor(pct) {
  if (pct >= 80) return "#1B1F4B";
  if (pct >= 50) return "#5B6196";
  if (pct >= 25) return "#9AA0C0";
  return "#D9D8E4";
}

function prettyAction(a) {
  if (!a) return "—";
  return a
    .replace(/_/g, " ")
    .replace(/\./g, " → ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
